import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { getRootUrl } from '@share-a-cart/shared-utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { TooltipStyled } from 'src/components';
import { VendorListAndPriceDisc } from 'src/components/cart/VendorListAndPriceDisc';
import { VendorLogoBlock } from 'src/components/cart/VendorLogoBlock';
import { useCopyToClipboard } from 'src/hooks';

interface Props {
  cartId: string;
  vendor: string;
  vendorDisplayName?: string;
  itemCount: number;
  timestamp?: number;
  store?: string;
  displaySourceSite?: string;
}

// Saved-cart header: cart-id chip, copy-link / share actions, and the
// "This is a {vendor} list. Prices as of {date}" notice with price disclaimer.
export const CartNotice = ({
  cartId,
  vendor,
  vendorDisplayName,
  itemCount,
  timestamp,
  store,
  displaySourceSite,
}: Props) => {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();

  const copyLink = () => copyToClipboard(`${getRootUrl()}/get/${cartId}`);

  return (
    // Always stacked: chip row (logo / id / copy / share) on top, notice text below.
    <div className='flex flex-col w-fit ml-auto items-end justify-end mt-auto gap-y-2 gap-x-2 pr-4'>
      <div className='flex flex-row w-fit justify-center items-center'>
        <div className='inline-flex w-fit items-center justify-center'>
          <VendorLogoBlock vendor={vendor} size='medium' />
          <span className='heading-200 px-2 py-[2.5px] text-primary-dark'>{cartId}</span>
          <TooltipStyled title={t('common.copy_link_to_list')}>
            <div
              onClick={copyLink}
              className='flex cursor-pointer items-center justify-center fill-offGray hover:fill-gray-300'
            >
              <ContentCopyIcon
                className='transition-all'
                style={{ fontSize: 20, fill: 'inherit' }}
              />
            </div>
          </TooltipStyled>
          <TooltipStyled title={t('common.share_list')}>
            <Link
              to={`/share/${cartId}`}
              reloadDocument
              className='ml-1 flex cursor-pointer items-center justify-center fill-offGray hover:fill-gray-300'
            >
              <ShareOutlinedIcon
                className='transition-all'
                style={{ fontSize: 20, fill: 'inherit' }}
              />
            </Link>
          </TooltipStyled>
        </div>
      </div>

      <VendorListAndPriceDisc
        timestamp={timestamp ?? 0}
        showPriceDateDisclaimer={timestamp !== undefined}
        itemCount={itemCount}
        cartVendor={vendor}
        vendorDisplayName={vendorDisplayName || vendor}
        displaySourceSite={displaySourceSite}
        cartStore={store}
        includeMarginRight={false}
      />
    </div>
  );
};
